import { normalizeFoodSearchText } from "../foods/supabaseFoodCatalogShared";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function collapseNormalizedSearchText(normalizedText) {
  return String(normalizedText ?? "").replace(/\s+/g, "");
}

function getWordBoundaryPosition(text, query) {
  const normalizedText = String(text ?? "");
  const normalizedQuery = String(query ?? "");

  if (!normalizedText || !normalizedQuery) {
    return Number.MAX_SAFE_INTEGER;
  }

  let bestPosition = Number.MAX_SAFE_INTEGER;
  const directPosition = normalizedText.indexOf(normalizedQuery);
  const boundaryPosition = normalizedText.indexOf(` ${normalizedQuery}`);

  if (directPosition === 0) {
    bestPosition = 0;
  }

  if (boundaryPosition >= 0) {
    bestPosition = Math.min(bestPosition, boundaryPosition + 1);
  }

  return bestPosition;
}

function getSignificantTokens(normalizedQuery) {
  const tokens = String(normalizedQuery ?? "")
    .split(" ")
    .map((token) => token.trim())
    .filter(Boolean);

  if (tokens.length <= 1) {
    return tokens;
  }

  return tokens.filter((token) => token.length >= 2);
}

function hasAllTokenPrefixes(words, tokens) {
  if (tokens.length === 0) {
    return false;
  }

  const resolvedWords = Array.isArray(words) ? words : [];
  if (resolvedWords.length === 0) {
    return false;
  }

  return tokens.every((token) =>
    resolvedWords.some((word) => word.startsWith(token)),
  );
}

function hasAllTokens(text, tokens) {
  if (tokens.length === 0) {
    return false;
  }

  const normalizedText = String(text ?? "");
  return tokens.every((token) => normalizedText.includes(token));
}

export function buildTextSearchIndex(parts = []) {
  const sourceText = Array.isArray(parts)
    ? parts.filter(hasText).join(" ")
    : String(parts ?? "");
  const normalizedText = normalizeFoodSearchText(sourceText);

  return {
    normalizedText,
    collapsedText: collapseNormalizedSearchText(normalizedText),
    words: normalizedText
      .split(" ")
      .map((word) => word.trim())
      .filter(Boolean),
  };
}

export function getTextSearchMatch(indexLike, query) {
  const index =
    indexLike && typeof indexLike === "object" && "normalizedText" in indexLike
      ? indexLike
      : buildTextSearchIndex(indexLike);
  const normalizedQuery = normalizeFoodSearchText(query);

  if (!normalizedQuery) {
    return null;
  }

  const collapsedQuery = collapseNormalizedSearchText(normalizedQuery);
  const significantTokens = getSignificantTokens(normalizedQuery);
  const matchedTokenCount = significantTokens.length;
  const phrasePrefixPosition = getWordBoundaryPosition(
    index?.normalizedText,
    normalizedQuery,
  );

  if (index?.normalizedText === normalizedQuery) {
    return {
      pattern: "exact",
      matchedTokenCount,
      prefixPosition: 0,
      matchLength: normalizedQuery.length,
    };
  }

  if (index?.collapsedText === collapsedQuery) {
    return {
      pattern: "exactCollapsed",
      matchedTokenCount,
      prefixPosition: 0,
      matchLength: collapsedQuery.length,
    };
  }

  if (phrasePrefixPosition !== Number.MAX_SAFE_INTEGER) {
    return {
      pattern: "phrasePrefix",
      matchedTokenCount,
      prefixPosition: phrasePrefixPosition,
      matchLength: normalizedQuery.length,
    };
  }

  const collapsedPosition = String(index?.collapsedText ?? "").indexOf(
    collapsedQuery,
  );
  if (collapsedQuery && collapsedPosition >= 0) {
    return {
      pattern: "collapsedContains",
      matchedTokenCount,
      prefixPosition: collapsedPosition,
      matchLength: collapsedQuery.length,
    };
  }

  if (hasAllTokenPrefixes(index?.words, significantTokens)) {
    return {
      pattern: "allTokenPrefixes",
      matchedTokenCount,
      prefixPosition: getWordBoundaryPosition(
        index?.normalizedText,
        significantTokens[0] ?? normalizedQuery,
      ),
      matchLength: significantTokens[0]?.length ?? normalizedQuery.length,
    };
  }

  if (hasAllTokens(index?.normalizedText, significantTokens)) {
    return {
      pattern: "allTokensPresent",
      matchedTokenCount,
      prefixPosition: getWordBoundaryPosition(
        index?.normalizedText,
        significantTokens[0] ?? normalizedQuery,
      ),
      matchLength: significantTokens[0]?.length ?? normalizedQuery.length,
    };
  }

  return null;
}

export function compareTextSearchMatches(leftMatch, rightMatch) {
  const priority = {
    exact: 0,
    exactCollapsed: 1,
    phrasePrefix: 2,
    collapsedContains: 3,
    allTokenPrefixes: 4,
    allTokensPresent: 5,
  };

  const leftPriority = priority[leftMatch?.pattern] ?? Number.MAX_SAFE_INTEGER;
  const rightPriority = priority[rightMatch?.pattern] ?? Number.MAX_SAFE_INTEGER;

  if (leftPriority !== rightPriority) {
    return leftPriority - rightPriority;
  }

  const leftTokens = Number(leftMatch?.matchedTokenCount ?? 0);
  const rightTokens = Number(rightMatch?.matchedTokenCount ?? 0);
  if (leftTokens !== rightTokens) {
    return rightTokens - leftTokens;
  }

  const leftPrefix = Number(leftMatch?.prefixPosition ?? Number.MAX_SAFE_INTEGER);
  const rightPrefix = Number(rightMatch?.prefixPosition ?? Number.MAX_SAFE_INTEGER);
  if (leftPrefix !== rightPrefix) {
    return leftPrefix - rightPrefix;
  }

  const leftLength = Number(leftMatch?.matchLength ?? Number.MAX_SAFE_INTEGER);
  const rightLength = Number(rightMatch?.matchLength ?? Number.MAX_SAFE_INTEGER);
  if (leftLength !== rightLength) {
    return leftLength - rightLength;
  }

  return 0;
}
